import { A2HS_COACH, API_BASE } from '@vibeshell/shared';

interface InstallCoachConfig {
  minEngagementSeconds: number;
  dismissalCooldownDays: number;
  storageKey: string;
}

declare global {
  interface Window {
    posthog?: { capture: (event: string, props?: Record<string, unknown>) => void };
    __VIBESHELL_APP_ID__?: string;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export class InstallCoach {
  private sessionStart = Date.now();
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor(private readonly config: InstallCoachConfig = { ...A2HS_COACH }) {
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      window.dispatchEvent(new Event('vs:install-available'));
    });
    window.addEventListener('appinstalled', () => {
      window.posthog?.capture('a2hs_completed', { platform: 'android' });
      reportInstall('a2hs');
    });
    this.detectIOSInstallCompletion();
  }

  shouldShow(): boolean {
    if (typeof window === 'undefined') return false;
    if (this.isStandalone()) return false;
    const lastDismissed = this.getLastDismissal();
    if (lastDismissed) {
      const daysSince = (Date.now() - lastDismissed) / 86_400_000;
      if (daysSince < this.config.dismissalCooldownDays) return false;
    }
    if (!this.isIOSSafari() && !this.isAndroidChrome()) return false;
    if (this.isInAppBrowser()) return false;
    if (this.engagementSeconds() < this.config.minEngagementSeconds) return false;
    return true;
  }

  isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  isIOSSafari(): boolean {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isIOS && isSafari;
  }

  isAndroidChrome(): boolean {
    const ua = navigator.userAgent;
    return /Android/.test(ua) && /Chrome/.test(ua);
  }

  isInAppBrowser(): boolean {
    const ua = navigator.userAgent;
    return /(FBAN|FBAV|Instagram|Twitter|Line|MicroMessenger)/.test(ua);
  }

  engagementSeconds(): number {
    return Math.floor((Date.now() - this.sessionStart) / 1000);
  }

  recordDismissal(): void {
    localStorage.setItem(this.config.storageKey, Date.now().toString());
    window.posthog?.capture('install_coach_dismissed', { platform: this.platformLabel() });
  }

  recordShown(): void {
    window.posthog?.capture('install_coach_shown', {
      platform: this.platformLabel(),
      engagementSeconds: this.engagementSeconds()
    });
  }

  recordTapThrough(): void {
    window.posthog?.capture('install_coach_tap_through', { platform: this.platformLabel() });
  }

  async triggerAndroidInstall(): Promise<boolean> {
    if (!this.deferredPrompt) return false;
    await this.deferredPrompt.prompt();
    const choice = await this.deferredPrompt.userChoice;
    window.posthog?.capture('a2hs_prompt_result', { outcome: choice.outcome });
    this.deferredPrompt = null;
    return choice.outcome === 'accepted';
  }

  hasNativePrompt(): boolean {
    return this.deferredPrompt !== null;
  }

  private platformLabel(): 'ios' | 'android' | 'unknown' {
    if (this.isIOSSafari()) return 'ios';
    if (this.isAndroidChrome()) return 'android';
    return 'unknown';
  }

  private getLastDismissal(): number | null {
    const v = localStorage.getItem(this.config.storageKey);
    return v ? parseInt(v, 10) : null;
  }

  private detectIOSInstallCompletion() {
    const wasStandalone = sessionStorage.getItem('vs:was-standalone') === 'true';
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone && !wasStandalone) {
      window.posthog?.capture('a2hs_completed', { platform: 'ios' });
      reportInstall('a2hs');
    }
    sessionStorage.setItem('vs:was-standalone', String(isStandalone));
  }
}

/**
 * Best-effort: record the install in the D1 analytics pipeline so the admin
 * dashboard's install metrics reflect real users. Fire-and-forget — a
 * telemetry failure must never affect the generated app.
 */
function reportInstall(source: 'a2hs' | 'deep-link'): void {
  const appId = window.__VIBESHELL_APP_ID__;
  if (!appId) return;
  try {
    void fetch(`${API_BASE}/v1/apps/${encodeURIComponent(appId)}/install`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ source }),
      keepalive: true
    }).catch(() => undefined);
  } catch {
    /* fire-and-forget */
  }
}
